import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { ROUTE_ROLES } from '@/constants/roles';
import { isFeatureEnabled } from '@/constants/featureFlags';

import sidebarData from './data';

/**
 * Hook customizado para gerenciar dados e estado da sidebar do dashboard
 * @returns {Object} Objeto contendo estado e funções da sidebar
 * @returns {boolean} returns.isLoading - Estado de carregamento
 * @returns {Array} returns.sideBarContent - Array com itens do menu da sidebar
 * @returns {boolean} returns.isSidebarExpanded - Estado de expansão da sidebar
 * @returns {number} returns.currentUserRole - Role do usuário atual
 * @returns {Function} returns.handleLeftSidebar - Função para alternar expansão da sidebar
 * @returns {Function} returns.handleClickMenu - Função para navegar entre menus
 */
export const useSidebarData = () => {
    const currentUserRole = Number(sessionStorage.getItem('currentUserRole'));
    const segment = useSelector((state) => state.auth.currentSegment);

    const navigate = useNavigate();
    // const location = useLocation();
    const [isLoading] = useState(false);
    const [sideBarContent, setSideBarContent] = useState([]);

    const { user } = useSelector((state) => state.auth);
    const consultantId = sessionStorage.getItem('consultant_id') ?? null;
    const [isSidebarExpanded, setSidebarExpanded] = useState(false);
    const viewAsConsultant = useSelector((state) => state.auth.viewAsConsultant);
    const { id: enterpriseId } = useParams();

    /**
     * Navega para uma localização específica
     * @param {string} location - Caminho para navegação
     */
    const handleClickMenu = (location) => {
        navigate(location);
    };

    /**
     * Obtém os itens da sidebar de forma assíncrona
     * @param {Array} existConsultantId - Array com IDs de consultores existentes
     * @param {string} role - Role do usuário
     * @returns {Promise<Array>} Promise que resolve com array de itens do menu
     */
    const getSiderBarItems = async (existConsultantId, role) => {
        return new Promise((resolve, reject) => {
            try {
                const menuItems = sidebarData(role, existConsultantId, enterpriseId || null);
                resolve(menuItems);
            } catch (error) {
                reject(error);
            }
        });
    };

    /**
     * Filtra itens do menu baseado nas permissões do usuário atual
     * @param {Array} menuItems - Array de itens do menu
     * @returns {Array} Array filtrado de itens do menu permitidos para o usuário
     */
    const handleCurrentUserMenuItems = (menuItems, existConsultantId) => {
        if (!menuItems.length) return [];

        let userRole = currentUserRole;
        if (existConsultantId.length) {
            userRole = 2;
        }
        // Quando um admin estiver dentro do contexto de uma empresa (id na URL),
        // tratar como consultor para exibir os ícones equivalentes na sidebar

        return menuItems.filter((menuItem) => {
            // Verifica permissões básicas
            const hasRolePermission = menuItem.allowedRoles?.includes(userRole) || typeof menuItem.allowedRoles === 'undefined';
            const notHiddenForEnterprise = !menuItem?.hideForEnterprise?.includes(user?.enterprise?.id);
            const hasSegmentPermission = menuItem.allowedSegment?.includes(segment) || typeof menuItem.allowedSegment === 'undefined';

            // Verifica feature flags específicas baseadas no path
            let hasFeaturePermission = true;

            if (menuItem.path?.includes('/agenda')) {
                hasFeaturePermission = isFeatureEnabled('AGENDA', segment);
            } else if (menuItem.path?.includes('/encontros-ao-vivo')) {
                hasFeaturePermission = isFeatureEnabled('EVENTOS_AO_VIVO', segment);
            }

            return hasRolePermission && notHiddenForEnterprise && hasSegmentPermission && hasFeaturePermission;
        });
    };

    /**
     * Preenche o conteúdo da sidebar com base no role e permissões do usuário
     * Obtém os itens do menu, filtra por permissões e atualiza o estado
     */
    const fillSideBarContent = async () => {
        let role = null;
        consultantId === null ? (role = ROUTE_ROLES[Number(sessionStorage.getItem('currentUserRole'))]) : (role = ROUTE_ROLES[2]);

        const existConsultantId = sessionStorage.getItem('consultant_id') ? [1, 4, 5, 9] : [];
        // let dataMenu;
        // segment == 2
        //     ? (dataMenu = sidebarDataGraos(role, existConsultantId, user?.enterprise?.id || null))
        //     : (dataMenu = sidebarData(role, existConsultantId, user?.enterprise?.id || null));

        const menuItems = await getSiderBarItems(existConsultantId, role);

        const dataMenuBySegment = handleCurrentUserMenuItems(menuItems, existConsultantId);

        // if (consultantId !== null) {
        //     if ([1, 9].includes(Number(sessionStorage.getItem('currentUserRole')))) {
        //         dataMenuBySegment.splice(3, 1);
        //     } else {
        //         dataMenuBySegment.splice(4, 1);
        //     }
        // }

        setSideBarContent(dataMenuBySegment);
    };

    /**
     * Alterna o estado de expansão da sidebar
     */
    const handleLeftSidebar = () => {
        setSidebarExpanded(!isSidebarExpanded);
    };

    useEffect(() => {
        fillSideBarContent();
    }, [viewAsConsultant, enterpriseId]);

    return useMemo(() => {
        return {
            isLoading,
            sideBarContent,
            isSidebarExpanded,
            currentUserRole,
            handleLeftSidebar,
            handleClickMenu
        };
    }, [isLoading, sideBarContent, isSidebarExpanded, currentUserRole, handleLeftSidebar, handleClickMenu]);
};
